"use client"
import {
  button_default
} from "./chunk-4RZJ54WQ.js";
import {
  icon_default
} from "./chunk-IWQIV3EZ.js";

// src/controls/slider/slider.tsx
import { trackEvent } from "@a24/analytics";
import { useCallback, useEffect, useRef, useState } from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var Slider = ({
  children,
  id,
  navigationClassName,
  showNavigation = true,
  sliderClassName,
  slidesRef
}) => {
  if (!children) return null;
  const [index, setIndex] = useState(0);
  const [isScrollMin, setIsScrollMin] = useState(true);
  const [isScrollMax, setIsScrollMax] = useState(false);
  const sliderRef = useRef(null);
  const isArtificialScroll = useRef(false);
  const scrollLeft = useRef(0);
  const scrollPadding = useRef(0);
  const onPrevClick = useCallback(() => {
    isArtificialScroll.current = true;
    setIndex(Math.max(0, index - 1));
    trackEvent({
      name: "button_interaction",
      data: { value: "slider--previous" }
    });
  }, [index]);
  const onNextClick = useCallback(() => {
    isArtificialScroll.current = true;
    setIndex(Math.min(index + 1, slidesRef.length - 1));
    trackEvent({ name: "button_interaction", data: { value: "slider--next" } });
  }, [slidesRef, index]);
  const computeScrollLeft = useCallback(() => {
    const group = slidesRef[index];
    scrollLeft.current = (group?.offsetLeft || 0) - scrollPadding.current;
  }, [index, slidesRef]);
  const scrollTo = () => {
    sliderRef.current?.scrollTo({
      left: scrollLeft.current,
      behavior: "smooth"
    });
  };
  const onScrollEnded = useCallback(() => {
    if (isArtificialScroll.current) {
      isArtificialScroll.current = false;
      return;
    }
    const scrollVal = sliderRef.current?.scrollLeft || 0;
    let elt = slidesRef[0] || null;
    for (let i = 0; i < slidesRef.length; i++) {
      const prev = elt;
      const next = slidesRef[i];
      const prevBounds = (prev?.offsetLeft || 0) - scrollVal;
      if (prevBounds >= 0) {
        break;
      }
      elt = next || null;
    }
    const closestGroupToLeftBound = slidesRef?.indexOf(elt);
    if (closestGroupToLeftBound > -1) {
      setIndex(closestGroupToLeftBound);
    }
  }, [slidesRef]);
  useEffect(() => {
    let scrollTimeout = 0;
    const onScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(onScrollEnded, 100);
    };
    sliderRef.current?.addEventListener("scroll", onScroll);
    return () => {
      sliderRef.current?.removeEventListener("scroll", onScroll);
    };
  }, [onScrollEnded]);
  useEffect(() => {
    computeScrollLeft();
    if (isArtificialScroll.current) {
      scrollTo();
    }
  }, [index]);
  function onResize() {
    if (sliderRef.current) {
      setTimeout(() => {
        scrollPadding.current = Number.parseInt(
          getComputedStyle(
            sliderRef.current
          ).scrollPadding.replace("px", ""),
          10
        ) || 0;
      }, 100);
    }
  }
  useEffect(() => {
    window.addEventListener("resize", onResize);
    onResize();
    const observers = [];
    slidesRef.forEach((slide, index2) => {
      if (!slide) {
        return;
      }
      const intersectionState = {
        isIn: false
      };
      const options = {
        rootMargin: "100% 0% 100% 0%",
        threshold: 1
      };
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            intersectionState.isIn = true;
          } else if (intersectionState.isIn === true) {
            intersectionState.isIn = false;
          }
          if (index2 === 0) {
            setIsScrollMin(intersectionState.isIn);
          }
          if (index2 === slidesRef.length - 1) {
            setIsScrollMax(intersectionState.isIn);
          }
        });
      }, options);
      observer.observe(slide);
      observers.push(observer);
    });
    return () => {
      window.removeEventListener("resize", onResize);
      observers.forEach((observer) => {
        observer.disconnect();
      });
    };
  }, [slidesRef]);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: `${navigationClassName}${!showNavigation ? " hidden md:hidden lg:hidden" : ""}`,
        children: [
          /* @__PURE__ */ jsx(
            button_default,
            {
              className: "button-arrow w-fit mb-4 mr-4 md:mt-[0.375rem] md:mb-[1.625rem] disabled:cursor-default",
              a11yLabel: "previous",
              onClick: onPrevClick,
              disabled: isScrollMin,
              icon: /* @__PURE__ */ jsx(icon_default, { name: "arrow-left" })
            }
          ),
          /* @__PURE__ */ jsx(
            button_default,
            {
              className: "button-arrow w-fit mb-4 md:mt-[0.375rem] md:mb-[1.625rem] disabled:cursor-default",
              a11yLabel: "next",
              onClick: onNextClick,
              disabled: isScrollMax,
              icon: /* @__PURE__ */ jsx(icon_default, { name: "arrow-right" })
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx("div", { className: `${sliderClassName} overflow-x-auto`, ref: sliderRef, children })
  ] });
};
var slider_default = Slider;

export {
  slider_default
};
